import Service, { inject as service } from '@ember/service';
import config from 'cio/config/environment';
import {
  init as filestackInit,
  Client,
  PickerResponse,
  PickerOptions,
  PickerFileMetadata,
  Security,
} from 'filestack-js';
import RavenService from 'cio/services/raven';
import ApiService from 'cio/services/api';
import { task } from 'ember-concurrency';
import { all, timeout } from 'ember-concurrency';

type SuccessCallback = (res: PickerResponse) => unknown;
type PickerFileErrorCallback = (file: PickerFileMetadata, error: Error) => unknown;

export default class FilepickerService extends Service {
  @service raven!: RavenService;
  @service api!: ApiService;

  filepicker: Client | null = null;
  security: Security | null = null;

  async createClient() {
    if (!config.isTest) {
      if (this.filepicker) {
        return;
      }

      let security = await this.api.request<Security>('filestack_security', { pathScope: 'global' });
      let filepicker = filestackInit(config.filestack.apikey, { security });

      this.setProperties({
        filepicker,
        security,
      });

      filepicker.on('upload.error', (error) => {
        this.raven.withScope((scope) => {
          scope.setExtra('policy', security.policy);
          this.raven.captureException(error);
        });
      });
    }
  }

  willDestroy() {
    super.willDestroy();

    if (!this.filepicker) {
      return;
    }

    this.filepicker.removeAllListeners();
  }

  pickAndStore(success: SuccessCallback, failure: PickerFileErrorCallback = () => {}) {
    if (!this.filepicker) {
      return;
    }

    let pickerOptions: PickerOptions = {
      accept: 'image/*',
      allowManualRetry: true,
      /* eslint-disable spellcheck/spell-checker */
      fromSources: [
        'local_file_system',
        'url',
        'imagesearch',
        'facebook',
        'instagram',
        'googledrive',
        'dropbox',
        'webcam',
        'box',
        'onedrive',
        'onedriveforbusiness',
        'picasa',
      ],
      /* eslint-enable spellcheck/spell-checker */
      onUploadDone: this.waitForKeyTask.perform.bind(this.waitForKeyTask, success),
      onFileUploadFailed: failure,
      transformations: {
        crop: true,
        rotate: true,
        circle: true,
      },
      storeTo: {
        location: config.filestack.location,
      },
    };

    this.filepicker.picker(pickerOptions).open();
  }

  waitForKeyTask = task(async (successCallback: SuccessCallback, response: PickerResponse) => {
    let filesWithoutKeys = response.filesUploaded.filter((file) => file.key == null);
    let filesWithKeys = response.filesUploaded.filter((file) => file.key != null);

    // When uploading images to Filestack, they first go through Filestack edge
    // servers and then get passed on to S3/GCS. The "onUploadDone" callback
    // fires when the call to the edge servers is successful, but that may mean
    // that the file hasn't yet been passed on to S3/GCS fully.
    // Files that haven't been fully transferred yet won't have a "key" present.
    // Here, we loop through the key-less images until a key is present, and
    // then proceed.
    while (filesWithoutKeys.length > 0) {
      await timeout(config.APP.filepickerPollingDelay);

      let refreshedFileMetadata = await all(
        filesWithoutKeys.map((file) => {
          if (!this.filepicker) {
            throw new Error('Filestack client not present');
          }

          return this.filepicker.metadata(file.handle);
        }),
      );
      filesWithoutKeys = refreshedFileMetadata.filter((file) => file.key == null);
      filesWithKeys = [...filesWithKeys, ...refreshedFileMetadata.filter((file) => file.key != null)];
    }

    return successCallback({
      filesUploaded: filesWithKeys,
      filesFailed: response.filesFailed,
    });
  });

  url(file: PickerFileMetadata) {
    if (!file.key) {
      throw new Error('A key was not found on the uploaded image');
    }

    // Use our CDN
    return `${config.filestack.cdn}/${encodeURIComponent(file.key)}`;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    'file-picker': FilepickerService;
  }
}
